/**
 * The words on the Bridge tab (docs/ux/bridge.md): the four steps, the
 * sub-state the rest of the site shows, and the job codes as sentences.
 * Never a file's contents.
 */

import type { BridgeAudioFile, BridgeMode, BridgeReturnedFile, BridgeTranscriptCounts, BridgeTranscriptMode, JobState } from '@/app/types';
import { pluralise } from '@/lib/format';

export const STEPS: ReadonlyArray<{ mode: Exclude<BridgeMode, 'none'>; word: string }> = [
  { mode: 'audio_in', word: 'Audio in' },
  { mode: 'packaged', word: 'Packaged' },
  { mode: 'returned', word: 'Returned' },
  { mode: 'ingested', word: 'Ingested' },
];

const ORDER: Record<BridgeMode, number> = { none: 0, audio_in: 1, packaged: 2, returned: 3, ingested: 4 };

export const reached = (mode: BridgeMode, step: BridgeMode): boolean => ORDER[mode] >= ORDER[step];

/** The sub-state queues and the hub carry while the scribe holds the report (D-0011 on OPEN 24). */
export function bridgeSubState(mode: BridgeMode | undefined): string | null {
  return mode === 'audio_in' || mode === 'packaged' || mode === 'returned' ? 'Awaiting transcription' : null;
}

export const BUSY_WORD: Record<JobState, string> = { queued: 'Queued.', running: 'Working.', succeeded: 'Done.', failed: 'Failed.' };

export function failureSentence(code: string | undefined): string {
  switch (code) {
    case 'PDF_CONVERTER_UNAVAILABLE': return 'This host has no document converter; the container image carries LibreOffice, the laptop does not.';
    case 'PDF_CONVERSION_FAILED': return 'LibreOffice did not produce the blank.';
    case 'LIBREOFFICE_TIMEOUT': return 'Rendering the blank took longer than two minutes and was stopped.';
    case 'NO_AUDIO': return 'No audio is in for this report.';
    case 'AUDIO_MISSING': return 'The stored audio could not be read.';
    case 'PACKAGE_EXISTS': return 'A package already exists for this report.';
    case 'NOT_A_DOCX': return 'The returned file could not be read as a Word document.';
    case 'EMPTY_DOCUMENT': return 'The returned document has no text.';
    case 'RETURNED_FILE_MISSING': return 'The stored returned file could not be read.';
    case 'WORKSPACE_LOCKED': return 'The report is complete; nothing can be ingested into it.';
    case 'WORKSPACE_MISSING': return 'The report has no workspace document.';
    case 'RENDER_FAILED': return 'The blank could not be rendered from the workspace.';
    case 'TRANSCRIPTION_OFF': return 'Machine transcription is not switched on in this environment.';
    case 'DEEPGRAM_KEY_MISSING': return 'This environment has no Deepgram key, so nothing was sent.';
    case 'DEEPGRAM_UNREACHABLE': return 'Deepgram Australia could not be reached.';
    case 'DEEPGRAM_TIMEOUT': return 'Deepgram Australia did not answer within five minutes.';
    case 'DEEPGRAM_REFUSED': return 'Deepgram Australia refused the request.';
    case 'DEEPGRAM_FAILED': return 'Deepgram Australia failed on the request.';
    case 'DEEPGRAM_RESPONSE_INVALID': return 'Deepgram Australia answered in a shape this portal could not read.';
    case 'RESIDENCY_REFUSED': return 'The transcription service was not the Australian one, so nothing was sent.';
    case 'EMPTY_TRANSCRIPT': return 'Nothing was heard in the recording.';
    case 'TRANSCRIPT_EXISTS': return 'A transcript already exists for this recording.';
    // A job that threw something other than a code (a storage hiccup, say) is recorded as FAILED by every bridge handler.
    case 'FAILED': return 'The step stopped before it finished.';
    default: return `The step failed (${code ?? 'no code'}).`;
  }
}

/** A recording's row (D-0088): nothing when no transcript was asked for, else being transcribed, transcribed with its word count, or why it failed. */
export function transcriptOutcome(file: BridgeAudioFile): string | undefined {
  const t = file.transcript;
  if (!t) return undefined;
  if (t.job.state === 'queued' || t.job.state === 'running') return 'Being transcribed';
  if (t.job.state === 'succeeded') return `Transcribed: ${pluralise(t.counts.words, 'word')}`;
  return `Failed: ${failureSentence(t.code ?? undefined)}`;
}

/** Who read the recording; the screen says so because a synthetic transcript must never pass for a real one. */
export function providerWord(mode: BridgeTranscriptMode | null): string {
  return mode === 'live' ? 'Deepgram Australia' : 'Synthetic sample';
}

/** "4 min 12 s", not the recorder's clock: the meta line already carries a timestamp, and two clock-shaped tokens read as one. */
export function durationWord(seconds: number | null): string {
  if (seconds === null) return 'length unknown';
  const whole = Math.max(0, Math.round(seconds));
  const m = Math.floor(whole / 60);
  const s = whole % 60;
  return m === 0 ? `${s} s` : `${m} min ${s} s`;
}

export function transcriptCountsLine(counts: BridgeTranscriptCounts): string {
  return [pluralise(counts.words, 'word'), pluralise(counts.paragraphs, 'paragraph'), `${pluralise(counts.doubtfulWords, 'doubtful word')}`, pluralise(counts.styleChanges, 'style change')].join(', ');
}

export const SHOWN_HERE_ONLY = 'Shown here only; nothing is written into the report.';

export const NEVER_RECORDED = 'The patient is never recorded. The audio and the returned template stay with the case as evaluation evidence; nothing is emailed.';

export function formatBytes(n: number): string {
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function elapsedWord(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** A returned file's row: not read yet, being read, read into n boxes, or why it failed (D-0057). */
export function outcomeSentence(file: BridgeReturnedFile): string {
  const ingest = file.ingest;
  if (!ingest) return 'Not read yet';
  if (ingest.job.state === 'queued' || ingest.job.state === 'running') return 'Being read';
  if (ingest.job.state === 'succeeded') return `Read into ${ingest.filled} ${ingest.filled === 1 ? 'box' : 'boxes'}, ${ingest.unmapped} unmapped`;
  return `Failed: ${failureSentence(ingest.code ?? undefined)}`;
}

export const REMOVE_BODY = 'The file is removed from this bridge. The file itself is kept as evaluation evidence and can be recovered by Beam.';
